import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface LeadershipSectionProps {
  className?: string;
}

interface LeaderProfile {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  bio: string;
  expertise: string[];
  linkedin: string;
}

const LeadershipSection = ({ className = '' }: LeadershipSectionProps) => {
  const leaders: LeaderProfile[] = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    role: 'Founding Partner & Chief Strategy Officer',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d07eb19-1763299289544.png",
    alt: 'Professional woman with short brown hair in navy blazer smiling confidently in modern office',
    bio: '15+ years leading enterprise AI transformations across financial services, healthcare, and manufacturing sectors. Former McKinsey Principal specializing in digital transformation and organizational change.',
    expertise: ['AI Strategy', 'Change Management', 'Executive Leadership'],
    linkedin: 'https://linkedin.com/in/sarahmitchell'
  },
  {
    id: 2,
    name: 'James Chen',
    role: 'Partner & Head of Delivery Excellence',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1de8d0400-1763294237371.png",
    alt: 'Asian man in charcoal suit with glasses looking professional in corporate setting',
    bio: 'Former VP of Digital Transformation at HSBC with 12 years experience implementing AI solutions at scale. Specializes in organizational readiness and capability building for AI adoption.',
    expertise: ['Implementation Strategy', 'Organizational Design', 'Technology Integration'],
    linkedin: 'https://linkedin.com/in/jameschen'
  },
  {
    id: 3,
    name: 'Dr. Emily Roberts',
    role: 'Partner & Chief Learning Officer',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8f896f2-1763299689286.png",
    alt: 'Woman with blonde hair in professional attire smiling warmly in bright office environment',
    bio: 'PhD in Organizational Psychology with 10 years consulting experience. Designed learning frameworks for AI adoption used by Fortune 500 companies. Expert in behavioral change and capability development.',
    expertise: ['Learning Design', 'Behavioral Science', 'Culture Transformation'],
    linkedin: 'https://linkedin.com/in/emilyroberts'
  }];


  return (
    <section className={`py-16 lg:py-24 bg-muted/30 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary mb-6">
            Leadership Team
          </h2>
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            Our founding partners bring decades of combined experience in AI transformation, change management, and organizational development from leading global consultancies and enterprises.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {leaders.map((leader) =>
          <div
            key={leader.id}
            className="bg-card rounded-lg overflow-hidden shadow-card hover:shadow-strong transition-all duration-300 border border-border">

              <div className="relative h-80 overflow-hidden">
                <AppImage
                src={leader.image}
                alt={leader.alt}
                className="w-full h-full object-cover" />

              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold font-headline text-foreground mb-1">
                  {leader.name}
                </h3>
                <p className="text-sm text-secondary font-semibold font-body mb-4">
                  {leader.role}
                </p>
                <p className="text-muted-foreground font-body text-sm leading-relaxed mb-4">
                  {leader.bio}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {leader.expertise.map((skill, index) =>
                <span
                  key={index}
                  className="px-3 py-1 bg-secondary/10 text-secondary text-xs font-medium font-body rounded-full">

                      {skill}
                    </span>
                )}
                </div>
                <a
                href={leader.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-secondary hover:text-secondary/80 transition-colors duration-300">

                  <Icon name="ArrowTopRightOnSquareIcon" size={16} variant="outline" />
                  <span className="text-sm font-medium font-body">LinkedIn Profile</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default LeadershipSection;